import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Modal, Select, Table, Tag, Switch, Button, Empty, Tooltip, ConfigProvider,
} from 'antd';
import {
  InboxOutlined, ReloadOutlined, CheckCircleOutlined,
  WarningOutlined, CloseCircleOutlined, ArrowRightOutlined,
} from '@ant-design/icons';
import useAxiosClient from '../../axios-client';
import { useStateContext } from '../../contexts/ContextProvider';
import { PRIMARY, SHELL_THEME, shellAntdTheme, shellVars, ringOf, MONO } from '../../shellTokens';
import { LOW_STOCK_KG } from './fabricShelves';
import '../Util/formControls.css';

const { Option } = Select;

export default function FabricStockInquiryModal({
  open,
  onClose,
  initialFabricTypeId = null,
  initialFabricPatternId = null,
  initialFabricColorId = null,
  onSelectShelf = null,
}) {
  const axiosClient = useAxiosClient();
  const { darkMode } = useStateContext();

  const [loadingOptions, setLoadingOptions] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    fabrics: [],
    patterns: [],
    colors: [],
    hasPatternless: true,
  });

  const [selectedFabricTypeId, setSelectedFabricTypeId] = useState(initialFabricTypeId);
  const [selectedPatternId, setSelectedPatternId] = useState(initialFabricPatternId);
  const [selectedColorId, setSelectedColorId] = useState(initialFabricColorId);
  const [inStockOnly, setInStockOnly] = useState(false);

  const [queryLoading, setQueryLoading] = useState(false);
  const [queryResult, setQueryResult] = useState({
    summary: { totalWeight: 0, totalRolls: 0, shelfCount: 0 },
    shelves: [],
  });

  // Sync initial values when modal opens
  useEffect(() => {
    if (open) {
      setSelectedFabricTypeId(initialFabricTypeId);
      setSelectedPatternId(initialFabricPatternId);
      setSelectedColorId(initialFabricColorId);
    }
  }, [open, initialFabricTypeId, initialFabricPatternId, initialFabricColorId]);

  // Load filter options once when modal opens
  useEffect(() => {
    if (!open) return;
    let live = true;
    setLoadingOptions(true);
    axiosClient.get('/fabricStock/filterOptions')
      .then((res) => {
        if (!live) return;
        setFilterOptions({
          fabrics: res.data.fabrics || [],
          patterns: res.data.patterns || [],
          colors: res.data.colors || [],
          hasPatternless: res.data.hasPatternless ?? true,
        });
      })
      .catch(() => {})
      .finally(() => {
        if (live) setLoadingOptions(false);
      });

    return () => { live = false; };
  }, [open, axiosClient]);

  // Available patterns conditioned on the selected fabric type
  const availablePatterns = useMemo(() => {
    if (!selectedFabricTypeId) {
      return filterOptions.patterns;
    }
    return filterOptions.patterns.filter((p) => p.fabricTypeId === selectedFabricTypeId);
  }, [filterOptions.patterns, selectedFabricTypeId]);

  // Query backend whenever filters change
  const fetchStock = useCallback(async () => {
    if (!open) return;
    setQueryLoading(true);
    try {
      const params = {};
      if (selectedFabricTypeId) params.fabricTypeId = selectedFabricTypeId;
      if (selectedPatternId !== null && selectedPatternId !== undefined) {
        params.fabricPatternId = selectedPatternId;
      }
      if (selectedColorId) params.fabricColorId = selectedColorId;
      if (inStockOnly) params.inStockOnly = 1;

      const res = await axiosClient.get('/fabricStock/query', { params });
      setQueryResult({
        summary: res.data.summary || { totalWeight: 0, totalRolls: 0, shelfCount: 0 },
        shelves: res.data.shelves || [],
      });
    } catch {
      setQueryResult({
        summary: { totalWeight: 0, totalRolls: 0, shelfCount: 0 },
        shelves: [],
      });
    } finally {
      setQueryLoading(false);
    }
  }, [open, selectedFabricTypeId, selectedPatternId, selectedColorId, inStockOnly, axiosClient]);

  useEffect(() => {
    if (open) {
      fetchStock();
    }
  }, [open, fetchStock]);

  const handleFabricTypeChange = (val) => {
    setSelectedFabricTypeId(val || null);
    setSelectedPatternId(null);
    setSelectedColorId(null);
  };

  const handlePatternChange = (val) => {
    setSelectedPatternId(val !== undefined ? val : null);
    setSelectedColorId(null);
  };

  const handleColorChange = (val) => {
    setSelectedColorId(val || null);
  };

  const handleReset = () => {
    setSelectedFabricTypeId(null);
    setSelectedPatternId(null);
    setSelectedColorId(null);
    setInStockOnly(false);
  };

  const selectedFabricName = useMemo(() => {
    if (!selectedFabricTypeId) return null;
    return filterOptions.fabrics.find((f) => f.id === selectedFabricTypeId)?.name || 'Fabric';
  }, [filterOptions.fabrics, selectedFabricTypeId]);

  const selectedPatternName = useMemo(() => {
    if (selectedPatternId === 'none') return 'Plain / No Pattern';
    if (!selectedPatternId) return null;
    return filterOptions.patterns.find((p) => p.id === selectedPatternId)?.name || 'Pattern';
  }, [filterOptions.patterns, selectedPatternId]);

  const selectedColorName = useMemo(() => {
    if (!selectedColorId) return null;
    return filterOptions.colors.find((c) => c.id === selectedColorId)?.name || 'Color';
  }, [filterOptions.colors, selectedColorId]);

  const activeBreadcrumbs = useMemo(() => {
    const parts = [];
    if (selectedFabricName) parts.push(selectedFabricName);
    if (selectedPatternName) parts.push(selectedPatternName);
    if (selectedColorName) parts.push(selectedColorName);
    return parts;
  }, [selectedFabricName, selectedPatternName, selectedColorName]);

  const themeVars = useMemo(() => ({
    ...shellVars(darkMode),
    '--focusRing': ringOf(PRIMARY),
  }), [darkMode]);
  const t = SHELL_THEME[darkMode ? 'dark' : 'light'];

  const columns = [
    {
      title: 'Fabric Type',
      dataIndex: 'fabricType',
      key: 'fabricType',
      width: 140,
      render: (text) => <span style={{ fontWeight: 600 }}>{text || '—'}</span>,
    },
    {
      title: 'Pattern',
      dataIndex: 'fabricPattern',
      key: 'fabricPattern',
      width: 170,
      render: (text, row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {row.fabricPatternUrl ? (
            <Tooltip title={<img src={row.fabricPatternUrl} alt={text} style={{ maxWidth: 160, maxHeight: 160, borderRadius: 4 }} />}>
              <img
                src={row.fabricPatternUrl}
                alt=""
                style={{ width: 22, height: 22, borderRadius: 4, objectFit: 'cover', border: '1px solid var(--border)' }}
              />
            </Tooltip>
          ) : (
            <span style={{ width: 22, height: 22, borderRadius: 4, background: 'var(--surface2)', display: 'inline-block' }} />
          )}
          <span>{text || <span style={{ color: 'var(--dim)', fontStyle: 'italic' }}>Plain</span>}</span>
        </div>
      ),
    },
    {
      title: 'Color',
      dataIndex: 'fabricColor',
      key: 'fabricColor',
      width: 140,
      render: (text) => <span>{text || '—'}</span>,
    },
    {
      title: 'On Hand (kg)',
      dataIndex: 'onHandWeight',
      key: 'onHandWeight',
      align: 'right',
      width: 125,
      render: (weight) => (
        <span style={{ fontFamily: MONO, fontWeight: 600 }}>
          {Number(weight).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
      ),
    },
    {
      title: 'Rolls',
      dataIndex: 'onHandRolls',
      key: 'onHandRolls',
      align: 'right',
      width: 85,
      render: (rolls) => (
        <span style={{ fontFamily: MONO, fontWeight: 500 }}>
          {Number(rolls).toLocaleString()}
        </span>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      align: 'center',
      width: 110,
      render: (_, row) => {
        if (row.onHandWeight <= 0) {
          return <Tag color="error" icon={<CloseCircleOutlined />}>Out</Tag>;
        }
        if (row.isLowStock) {
          return <Tag color="warning" icon={<WarningOutlined />}>Low</Tag>;
        }
        return <Tag color="success" icon={<CheckCircleOutlined />}>Stocked</Tag>;
      },
    },
    ...(onSelectShelf ? [{
      title: 'Action',
      key: 'action',
      width: 90,
      align: 'center',
      render: (_, row) => (
        <Button
          type="primary"
          size="small"
          onClick={() => {
            onSelectShelf(row);
            onClose();
          }}
          icon={<ArrowRightOutlined />}
        >
          Use
        </Button>
      ),
    }] : []),
  ];

  const isExactShelf = selectedFabricTypeId && selectedColorId && selectedPatternId !== null;

  return (
    <ConfigProvider theme={shellAntdTheme(darkMode)}>
      <Modal
        title={(
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <InboxOutlined style={{ fontSize: 18, color: PRIMARY }} />
            <span style={{ font: '700 16px Outfit, sans-serif', color: t.text }}>Fabric Stock Inquiry</span>
          </div>
        )}
        open={open}
        onCancel={onClose}
        width={860}
        footer={null}
        destroyOnClose
        style={themeVars}
        wrapClassName="et-controls"
        className="et-controls et-fabric-inquiry-modal"
      >
        <style>{`
          .et-fabric-inquiry-modal {
            font-family: Outfit, system-ui, sans-serif;
            color: var(--text);
          }
          .et-fabric-inquiry-modal .ant-select:not(.ant-select-customize-input) .ant-select-selector {
            background: var(--surface2) !important;
            border: 1px solid var(--border) !important;
            border-radius: 8px !important;
            height: 38px !important;
            display: flex !important;
            align-items: center !important;
            padding: 0 11px !important;
          }
          .et-fabric-inquiry-modal .ant-select-single .ant-select-selector .ant-select-selection-item {
            display: flex !important;
            align-items: center !important;
            line-height: normal !important;
            font-family: Outfit, system-ui, sans-serif !important;
            font-size: 13px !important;
            font-weight: 500 !important;
            color: var(--text) !important;
          }
          .et-fabric-inquiry-modal .ant-select-single .ant-select-selector .ant-select-selection-placeholder {
            display: flex !important;
            align-items: center !important;
            line-height: normal !important;
            font-family: Outfit, system-ui, sans-serif !important;
            font-size: 13px !important;
            color: var(--dim) !important;
          }
          .et-fabric-inquiry-modal .ant-select-arrow {
            color: var(--dim) !important;
          }
          .et-fabric-inquiry-modal .ant-select-focused:not(.ant-select-disabled) .ant-select-selector {
            border-color: var(--primary) !important;
            box-shadow: 0 0 0 3px var(--focusRing, rgba(217, 119, 6, 0.12)) !important;
          }
        `}</style>

        <div className="et-controls" style={{ ...themeVars, display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 4 }}>
          {/* Dropdown Pickers Row */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr auto',
              gap: 12,
              alignItems: 'end',
              background: t.surface2,
              padding: '12px 14px',
              borderRadius: 10,
              border: `1px solid ${t.border}`,
            }}
          >
            <div>
              <label style={{ display: 'block', font: '700 11px Outfit, sans-serif', color: t.dim, textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 6 }}>
                1. FABRIC TYPE
              </label>
              <Select
                showSearch
                allowClear
                placeholder="Select fabric (e.g. Fujian)"
                value={selectedFabricTypeId}
                onChange={handleFabricTypeChange}
                loading={loadingOptions}
                filterOption={(input, option) =>
                  (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                }
                style={{ width: '100%' }}
              >
                {filterOptions.fabrics.map((f) => (
                  <Option key={f.id} value={f.id}>{f.name}</Option>
                ))}
              </Select>
            </div>

            <div>
              <label style={{ display: 'block', font: '700 11px Outfit, sans-serif', color: t.dim, textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 6 }}>
                2. PATTERN / PRINT
              </label>
              <Select
                showSearch
                allowClear
                placeholder={selectedFabricTypeId ? "All patterns" : "Choose fabric first"}
                value={selectedPatternId}
                onChange={handlePatternChange}
                disabled={!selectedFabricTypeId}
                optionLabelProp="label"
                filterOption={(input, option) =>
                  (option?.data_name ?? '').toLowerCase().includes(input.toLowerCase())
                }
                style={{ width: '100%' }}
              >
                {filterOptions.hasPatternless && (
                  <Option key="none" value="none" label="Plain / No Pattern" data_name="Plain / No Pattern">
                    <em>Plain / No Pattern</em>
                  </Option>
                )}
                {availablePatterns.map((p) => (
                  <Option key={p.id} value={p.id} label={p.name} data_name={p.name}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {p.url && (
                        <img
                          src={p.url}
                          alt=""
                          style={{ width: 16, height: 16, borderRadius: 2, objectFit: 'cover' }}
                        />
                      )}
                      <span>{p.name}</span>
                    </div>
                  </Option>
                ))}
              </Select>
            </div>

            <div>
              <label style={{ display: 'block', font: '700 11px Outfit, sans-serif', color: t.dim, textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 6 }}>
                3. COLOR
              </label>
              <Select
                showSearch
                allowClear
                placeholder={selectedFabricTypeId ? "All colors" : "Choose fabric first"}
                value={selectedColorId}
                onChange={handleColorChange}
                disabled={!selectedFabricTypeId}
                filterOption={(input, option) =>
                  (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                }
                style={{ width: '100%' }}
              >
                {filterOptions.colors.map((c) => (
                  <Option key={c.id} value={c.id}>{c.name}</Option>
                ))}
              </Select>
            </div>

            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button
                type="button"
                onClick={handleReset}
                style={{
                  height: 38,
                  padding: '0 14px',
                  borderRadius: 8,
                  border: `1px solid ${t.border}`,
                  background: t.surface,
                  color: t.text,
                  font: '600 12.5px Outfit, sans-serif',
                  cursor: 'pointer',
                  transition: 'all .12s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                Reset
              </button>
            </div>
          </div>

          {/* Rollup Summary Card */}
          <div
            style={{
              padding: '14px 18px',
              borderRadius: 10,
              background: t.surface2,
              border: `1px solid ${t.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 16,
              flexWrap: 'wrap',
            }}
          >
            <div>
              <div style={{ fontSize: 12, color: 'var(--dim)', fontWeight: 500, marginBottom: 2 }}>
                {activeBreadcrumbs.length > 0 ? (
                  <span>
                    Querying: <strong style={{ color: 'var(--text)' }}>{activeBreadcrumbs.join(' → ')}</strong>
                  </span>
                ) : (
                  <span>Overall Fabric Store Total</span>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 14 }}>
                <span style={{ fontSize: 24, fontWeight: 700, fontFamily: MONO, color: PRIMARY }}>
                  {queryResult.summary.totalWeight.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--dim)', marginLeft: 4 }}>kg</span>
                </span>
                <span style={{ fontSize: 16, fontWeight: 600, fontFamily: MONO, color: 'var(--text)' }}>
                  {queryResult.summary.totalRolls.toLocaleString()}
                  <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--dim)', marginLeft: 4 }}>rolls</span>
                </span>
                <span style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 500 }}>
                  ({queryResult.summary.shelfCount} {queryResult.summary.shelfCount === 1 ? 'shelf' : 'shelves'})
                </span>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              {isExactShelf && (
                <div>
                  {queryResult.summary.totalWeight <= 0 ? (
                    <Tag color="error" style={{ fontSize: 13, padding: '4px 10px' }}>Out of Stock</Tag>
                  ) : queryResult.summary.totalWeight < LOW_STOCK_KG ? (
                    <Tag color="warning" style={{ fontSize: 13, padding: '4px 10px' }}>Low Stock (&lt; 200 kg)</Tag>
                  ) : (
                    <Tag color="success" style={{ fontSize: 13, padding: '4px 10px' }}>In Stock</Tag>
                  )}
                </div>
              )}

              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--dim)' }}>
                <span>In-stock only</span>
                <Switch
                  size="small"
                  checked={inStockOnly}
                  onChange={setInStockOnly}
                />
              </div>

              <Button
                icon={<ReloadOutlined />}
                size="small"
                onClick={fetchStock}
                loading={queryLoading}
                title="Refresh stock"
              />
            </div>
          </div>

          {/* Shelves Breakdown Table */}
          <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
            <Table
              dataSource={queryResult.shelves}
              columns={columns}
              rowKey="id"
              loading={queryLoading}
              pagination={queryResult.shelves.length > 8 ? { pageSize: 8, size: 'small' } : false}
              size="small"
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      selectedFabricTypeId
                        ? "No shelves found matching the selected criteria"
                        : "Select a fabric type above to query stock"
                    }
                  />
                ),
              }}
            />
          </div>
        </div>
      </Modal>
    </ConfigProvider>
  );
}
